import React, { useState, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  closestCorners,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDroppable } from '@dnd-kit/core';
import { getTenderPipeline, moveTenderStage } from '../services/api';
import './TenderKanban.css';

const STAGE_META = [
  { key: 'discovery',  label: 'Discovery',  color: '#3b82f6' },
  { key: 'evaluation', label: 'Evaluation', color: '#f59e0b' },
  { key: 'bid_prep',   label: 'Bid Prep',   color: '#6366f1' },
  { key: 'submitted',  label: 'Submitted',  color: '#10b981' },
  { key: 'won',        label: 'Won',        color: '#059669' },
  { key: 'lost',       label: 'Lost',       color: '#ef4444' },
];

function formatCurrency(v) {
  if (!v && v !== 0) return '—';
  return '₹' + Number(v).toLocaleString('en-IN');
}

function formatDate(d) {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  } catch { return ''; }
}

function KanbanCard({ card, overlay }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: card.id + ':' + card.collection,
    data: { card },
  });

  const style = overlay ? {} : {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  const nextAction = card.next_action;

  return (
    <div
      ref={overlay ? undefined : setNodeRef}
      className={`kanban-card ${overlay ? 'kanban-card-overlay' : ''}`}
      style={style}
      {...(overlay ? {} : { ...attributes, ...listeners })}
    >
      <div className="kanban-card-ref">{card.tender_ref}</div>
      <div className="kanban-card-title">{card.title}</div>
      {card.buyer_name && (
        <div className="kanban-card-buyer">{card.buyer_name}</div>
      )}
      <div className="kanban-card-meta">
        {card.tender_value ? (
          <span className="kanban-card-value">{formatCurrency(card.tender_value)}</span>
        ) : null}
        {card.submission_deadline && (
          <span className="kanban-card-deadline">
            Due {formatDate(card.submission_deadline)}
          </span>
        )}
      </div>
      {nextAction && (
        <div className="kanban-card-next-action">
          <span className="next-action-icon">→</span>
          <span className="next-action-text">{nextAction.title}</span>
          {nextAction.due_date && (
            <span className="next-action-due">{formatDate(nextAction.due_date)}</span>
          )}
          {nextAction.assigned_to && (
            <span className="next-action-owner">{nextAction.assigned_to}</span>
          )}
        </div>
      )}
    </div>
  );
}

function StageColumn({ stage, cards }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.key });

  return (
    <div className={`kanban-column ${isOver ? 'kanban-column-over' : ''}`} ref={setNodeRef}>
      <div className="kanban-column-header" style={{ borderTopColor: stage.color }}>
        <span className="kanban-column-label">{stage.label}</span>
        <span className="kanban-column-count">{cards.length}</span>
      </div>
      <SortableContext
        items={cards.map(c => c.id + ':' + c.collection)}
        strategy={verticalListSortingStrategy}
      >
        <div className="kanban-column-body">
          {cards.map(card => (
            <KanbanCard key={card.id + ':' + card.collection} card={card} />
          ))}
          {cards.length === 0 && (
            <div className="kanban-empty">No tenders</div>
          )}
        </div>
      </SortableContext>
    </div>
  );
}

function TenderKanban() {
  const [stages, setStages] = useState({});
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeCard, setActiveCard] = useState(null);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const fetchData = useCallback(async () => {
    try {
      const data = await getTenderPipeline();
      setStages(data.stages || {});
      setSummary(data.summary || {});
      setError(null);
    } catch (err) {
      setError(err.message || 'Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  function handleDragStart(event) {
    const card = event.active.data?.current?.card;
    if (card) setActiveCard(card);
  }

  function handleDragCancel() {
    setActiveCard(null);
  }

  async function handleDragEnd(event) {
    setActiveCard(null);
    const { active, over } = event;
    if (!over) return;

    const card = active.data?.current?.card;
    if (!card) return;

    const toStage = over.id;
    if (!STAGE_META.find(s => s.key === toStage)) return;
    if (card.stage === toStage) return;

    const oldStages = { ...stages };
    setStages(prev => {
      const next = {};
      for (const key of Object.keys(prev)) {
        next[key] = prev[key].filter(c => c.id !== card.id);
      }
      const updatedCard = { ...card, stage: toStage };
      if (!next[toStage]) next[toStage] = [];
      next[toStage] = [...next[toStage], updatedCard];
      return next;
    });

    try {
      await moveTenderStage(card.id, card.collection, toStage);
      await fetchData();
    } catch (err) {
      setStages(oldStages);
      alert('Move failed: ' + (err.message || 'Unknown error'));
    }
  }

  if (loading) {
    return <div className="kanban-loading">Loading pipeline...</div>;
  }

  if (error) {
    return (
      <div className="kanban-error">
        <p>{error}</p>
        <button onClick={fetchData} className="kanban-retry-btn">Retry</button>
      </div>
    );
  }

  return (
    <div className="tender-kanban">
      <div className="kanban-summary-bar">
        {STAGE_META.map(s => {
          const info = summary[s.key] || { count: 0, total_value: 0 };
          return (
            <div className="kanban-summary-item" key={s.key}>
              <span className="summary-dot" style={{ background: s.color }} />
              <span className="summary-label">{s.label}</span>
              <span className="summary-count">{info.count}</span>
              {info.total_value > 0 && (
                <span className="summary-value">{formatCurrency(info.total_value)}</span>
              )}
            </div>
          );
        })}
      </div>

      <DndContext
        sensors={sensors}
        collisionDetection={closestCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        <div className="kanban-board">
          {STAGE_META.map(stage => (
            <StageColumn
              key={stage.key}
              stage={stage}
              cards={stages[stage.key] || []}
            />
          ))}
        </div>

        <DragOverlay dropAnimation={null}>
          {activeCard ? <KanbanCard card={activeCard} overlay /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

export default TenderKanban;
